import React, { useState } from 'react';

import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import { useBulkDismissViolationsMutation } from '../../../../redux/apis/attendanceViolationApi';
import { notification } from '../../../../utils/notification';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const pluralize = (count) => `${count} violation${count === 1 ? '' : 's'}`;

const ViolationBulkActionBar = ({ selectedIds, selectableIds, onSelectAll, onClear, onResult }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [bulkDismiss, { isLoading }] = useBulkDismissViolationsMutation();

  const selectedCount = selectedIds.length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;

  if (!selectableIds.length) return null;

  const handleBulkDismiss = async () => {
    try {
      const res = await bulkDismiss(selectedIds);
      if (res?.error) {
        notification(
          res?.error?.data?.statusMessage || 'Failed to dismiss violations.',
          TOAST_ERROR
        );
        return;
      }

      const succeeded = res?.data?.data?.succeeded || [];
      const failed = res?.data?.data?.failed || [];

      if (succeeded.length) {
        notification(`${pluralize(succeeded.length)} dismissed.`, TOAST_SUCCESS);
      }
      if (failed.length) {
        const reason = failed[0]?.message ? ` ${failed[0].message}.` : '';
        notification(`Failed to dismiss ${pluralize(failed.length)}.${reason}`, TOAST_ERROR);
      }

      setShowConfirm(false);
      onResult?.({ succeeded, failed: failed.map((item) => item?.id).filter(Boolean) });
    } catch (error) {
      notification(error?.message || 'Failed to dismiss violations.', TOAST_ERROR);
    }
  };

  return (
    <>
      <div
        className={`att-violation-bulk-bar${selectedCount ? ' att-violation-bulk-bar--active' : ''}`}
      >
        <label className="att-violation-bulk-bar__select-all">
          <input
            type="checkbox"
            className="form-check-input att-violation-check"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = selectedCount > 0 && !allSelected;
            }}
            onChange={() => (allSelected ? onClear() : onSelectAll())}
            disabled={isLoading}
          />
          <span className="att-violation-bulk-bar__text">
            {selectedCount ? (
              <>
                <span className="att-violation-bulk-bar__title">
                  <strong>{selectedCount}</strong> of {selectableIds.length} selected
                </span>
                <span className="att-violation-bulk-bar__hint">Ready to dismiss</span>
              </>
            ) : (
              <>
                <span className="att-violation-bulk-bar__title">Select all on page</span>
                <span className="att-violation-bulk-bar__hint">
                  {pluralize(selectableIds.length)} can be dismissed
                </span>
              </>
            )}
          </span>
        </label>

        {selectedCount ? (
          <div className="att-violation-bulk-bar__actions">
            <button
              type="button"
              className="att-violation-bulk-bar__btn att-violation-bulk-bar__btn--ghost"
              onClick={onClear}
              disabled={isLoading}
            >
              <i className="bi bi-x-lg" aria-hidden />
              Clear
            </button>
            <button
              type="button"
              className="att-violation-bulk-bar__btn att-violation-bulk-bar__btn--danger"
              onClick={() => setShowConfirm(true)}
              disabled={isLoading}
            >
              <i className="bi bi-trash3" aria-hidden />
              Dismiss
              <span className="att-violation-bulk-bar__count">{selectedCount}</span>
            </button>
          </div>
        ) : (
          <span className="att-violation-bulk-bar__tip">
            <i className="bi bi-info-circle" aria-hidden />
            Tick employees or individual dates to dismiss in bulk
          </span>
        )}
      </div>

      <ConfirmDialog
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={handleBulkDismiss}
        title={`Dismiss ${pluralize(selectedCount)}?`}
        message="This will permanently remove the selected violation logs. This action cannot be undone."
        confirmLabel="Dismiss all"
        cancelLabel="Cancel"
        variant="danger"
        loading={isLoading}
      />
    </>
  );
};

export default ViolationBulkActionBar;
